import { styled } from '@ltvco/refresh-lib/theme';

/**
 * Static illustrations of Claude's own dark UI. The colors depict a third-party
 * product, so they intentionally live here rather than in the brand theme.
 */
export const CLAUDE_UI = {
  window: '#262624',
  sidebar: '#1F1E1D',
  titleBar: '#141413',
  bubble: '#30302E',
  border: '#3A3935',
  text: '#F5F4EE',
  muted: '#A8A69E',
  accentText: '#E1C7F3',
  accentBg: 'rgba(183,131,220,0.22)',
};

interface CompactProps {
  compact?: boolean;
}

const compactOnly = { shouldForwardProp: (prop: string) => prop !== 'compact' };

export const MockWindow = styled('div')({
  position: 'relative',
  display: 'flex',
  overflow: 'hidden',
  backgroundColor: CLAUDE_UI.window,
  border: `1px solid ${CLAUDE_UI.border}`,
  borderRadius: '12px',
  textAlign: 'left',
});

export const MockSidebar = styled(
  'div',
  compactOnly,
)<CompactProps>(({ compact }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: compact ? 6 : 2,
  flexShrink: 0,
  width: compact ? 64 : 140,
  padding: compact ? '10px 6px' : '14px 10px',
  backgroundColor: CLAUDE_UI.sidebar,
  borderRight: `1px solid ${CLAUDE_UI.border}`,
}));

export const MockBrand = styled(
  'div',
  compactOnly,
)<CompactProps>(({ compact }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: compact ? 4 : 6,
  padding: compact ? 0 : '0 6px 12px',
  color: CLAUDE_UI.text,
  fontSize: compact ? 9 : 13,
  fontWeight: 700,
}));

export const SidebarItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'tone',
})<{ size: number; tone?: 'neutral' | 'accent' }>(({ size, tone }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: Math.round(size / 2),
  padding: Math.round(size / 2.2),
  borderRadius: 6,
  fontSize: size,
  color: CLAUDE_UI.muted,
  ...(tone === 'neutral' && {
    color: CLAUDE_UI.text,
    backgroundColor: 'rgba(255,255,255,0.08)',
  }),
  ...(tone === 'accent' && {
    color: CLAUDE_UI.accentText,
    backgroundColor: CLAUDE_UI.accentBg,
  }),
}));

export const MockChat = styled(
  'div',
  compactOnly,
)<CompactProps>(({ compact }) => ({
  flex: 1,
  minWidth: 0,
  padding: compact ? '10px 12px' : '20px 24px',
}));

export const UserRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: 12,
});

export const UserBubble = styled(
  'div',
  compactOnly,
)<CompactProps>(({ compact }) => ({
  maxWidth: '85%',
  padding: compact ? '6px 9px' : '10px 14px',
  borderRadius: compact ? 9 : 14,
  backgroundColor: CLAUDE_UI.bubble,
  color: CLAUDE_UI.text,
  fontSize: compact ? 10 : 13,
}));

export const AssistantRow = styled(
  'div',
  compactOnly,
)<CompactProps>(({ compact }) => ({
  display: 'flex',
  gap: compact ? 6 : 10,
}));

export const AssistantAvatar = styled(
  'div',
  compactOnly,
)<CompactProps>(({ theme, compact }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: compact ? 16 : 24,
  height: compact ? 16 : 24,
  borderRadius: '100px',
  backgroundColor: theme.palette.primary.main,
}));

export const AssistantText = styled(
  'div',
  compactOnly,
)<CompactProps>(({ compact }) => ({
  color: CLAUDE_UI.text,
  fontSize: compact ? 10 : 13,
  lineHeight: compact ? '15px' : '20px',
}));

export const MutedLines = styled('div')({ color: CLAUDE_UI.muted });

export const StrongValue = styled('strong')({ color: '#fff' });

export const TitleBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '10px 14px',
  backgroundColor: CLAUDE_UI.titleBar,
});

export const WindowDots = styled('div')({ display: 'flex', gap: 6 });

export const WindowDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  width: 10,
  height: 10,
  borderRadius: '100px',
  backgroundColor: color,
}));

export const BrowserTab = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 12px',
  borderRadius: '8px 8px 0 0',
  backgroundColor: CLAUDE_UI.window,
  color: CLAUDE_UI.text,
  fontSize: 11,
});

export const AddressBar = styled('div')({
  padding: '6px 14px',
  backgroundColor: CLAUDE_UI.sidebar,
  borderBottom: `1px solid ${CLAUDE_UI.border}`,
  color: CLAUDE_UI.muted,
  fontSize: 11,
});

export const ConnectorsWindow = styled('div')({
  height: 340,
  overflow: 'hidden',
  backgroundColor: CLAUDE_UI.window,
  border: `1px solid ${CLAUDE_UI.border}`,
  borderRight: 0,
  borderBottom: 0,
  borderRadius: '12px 0 0 0',
  textAlign: 'left',
});

export const ConnectorsBody = styled('div')({
  display: 'flex',
  height: '100%',
});

export const ConnectorsNav = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: 130,
  padding: '14px 12px',
  borderRight: `1px solid ${CLAUDE_UI.border}`,
});

export const NavTitle = styled('div')({
  paddingBottom: 10,
  color: CLAUDE_UI.text,
  fontSize: 12,
  fontWeight: 700,
});

export const ConnectorsContent = styled('div')({
  flex: 1,
  minWidth: 0,
  padding: 18,
});

export const ContentTitle = styled('div')({
  marginBottom: 12,
  color: CLAUDE_UI.text,
  fontSize: 13,
  fontWeight: 700,
});

export const AddConnectorButton = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  padding: '6px 12px',
  borderRadius: '100px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontSize: 11,
  fontWeight: 700,
}));

export const ConnectorForm = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 14,
  padding: 12,
  border: `1px solid ${CLAUDE_UI.border}`,
  borderRadius: 10,
  backgroundColor: CLAUDE_UI.bubble,
});

export const FieldLabel = styled('div')({
  color: CLAUDE_UI.muted,
  fontSize: 10,
});

export const FieldValue = styled('div', {
  shouldForwardProp: (prop) => prop !== 'mono',
})<{ mono?: boolean }>(({ mono }) => ({
  padding: '5px 8px',
  border: `1px solid ${CLAUDE_UI.border}`,
  borderRadius: 6,
  color: CLAUDE_UI.text,
  fontSize: mono ? 11 : 12,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  ...(mono && { fontFamily: 'ui-monospace, monospace' }),
}));
